import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { getPublicMenu, mediaUrl } from "@/api";
import { BrandScope } from "@/theme";
import { Loader2, ChevronDown } from "lucide-react";
import bonsteloLogo from "@/assets/brand/bonstelo-logo-full-transparent.png";

const eur = new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" });
const fmtPrice = (c) => eur.format((Number(c) || 0) / 100);
const secId = (id) => `menu-cat-${id}`;
const DIET_LABEL = { vegetarian: "Vegetarisch", vegan: "Vegan" };
const ALLERGEN_LABEL = {
  gluten: "Glutenhaltiges Getreide", crustaceans: "Krebstiere", eggs: "Eier", fish: "Fisch", peanuts: "Erdnüsse",
  soy: "Soja", milk: "Milch / Laktose", nuts: "Schalenfrüchte", celery: "Sellerie", mustard: "Senf", sesame: "Sesam",
  sulphites: "Schwefeldioxid / Sulfite", lupin: "Lupinen", molluscs: "Weichtiere",
};

const MenuItem = ({ it }) => {
  const [open, setOpen] = useState(false);
  const variants = it.price_variants || [];
  const allergens = (it.allergens || []).filter((a) => ALLERGEN_LABEL[a]);
  const diet = DIET_LABEL[it.dietary];
  return (
    <div data-testid="menu-public-item" className="grid grid-cols-[1fr_auto] gap-x-4 items-baseline">
      <p className={`font-display font-bold text-slate-900 text-[15px] leading-snug break-words ${variants.length ? "col-span-2" : ""}`}>
        {it.name}
        {diet && (
          <span data-testid="menu-public-dietary" className="ml-2 inline-block align-[2px] rounded-full bg-emerald-50 text-emerald-700 px-2 py-px text-[10.5px] font-bold leading-4 whitespace-nowrap">{diet}</span>
        )}
      </p>
      {!variants.length && (
        <p data-testid="menu-public-price" className="font-display font-bold text-slate-900 text-[15px] tabular-nums whitespace-nowrap text-right">{fmtPrice(it.price_cents)}</p>
      )}
      {it.description && (
        <p className="col-start-1 text-sm text-slate-500 leading-relaxed mt-0.5 break-words">{it.description}</p>
      )}
      {variants.map((v, i) => (
        <div key={i} data-testid="menu-public-variant" className="col-span-2 grid grid-cols-[1fr_auto] gap-x-4 items-baseline mt-0.5">
          <span className="text-sm text-slate-700 font-medium break-words">{v.label}</span>
          <span className="font-display font-bold text-slate-900 text-[15px] tabular-nums whitespace-nowrap text-right">{fmtPrice(v.price_cents)}</span>
        </div>
      ))}
      {it.note && (
        <p data-testid="menu-public-note" className="col-span-2 text-[13px] text-slate-500 italic mt-1 break-words">{it.note}</p>
      )}
      {allergens.length > 0 && (
        <div className="col-span-2 mt-1">
          <button type="button" data-testid="menu-public-allergen-toggle" aria-expanded={open}
            aria-label={`Allergene für ${it.name} ${open ? "ausblenden" : "anzeigen"}`} onClick={() => setOpen((o) => !o)}
            className="inline-flex items-center gap-1 text-xs font-semibold text-slate-400 hover:text-slate-600 py-0.5 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-300">
            Allergene <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
          </button>
          {open && (
            <p data-testid="menu-public-allergens" className="text-xs text-slate-500 leading-relaxed break-words">
              {allergens.map((a) => ALLERGEN_LABEL[a]).join(" · ")}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export const MenuScreen = () => {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    getPublicMenu(slug)
      .then(setData)
      .catch((e) => { if (e?.response?.status === 404) setNotFound(true); else setError(true); });
  }, [slug]);

  const scrollTo = (id) => {
    const el = document.getElementById(secId(id));
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  if (notFound) return (
    <div data-testid="menu-not-found" className="min-h-[100dvh] flex items-center justify-center p-6 text-center bg-slate-50">
      <div>
        <p className="font-display font-extrabold text-xl text-slate-900">Betrieb nicht gefunden</p>
        <p className="text-slate-500 text-sm mt-1">Dieser Link ist ungültig.</p>
      </div>
    </div>
  );
  if (error) return (
    <div data-testid="menu-load-error" className="min-h-[100dvh] flex items-center justify-center p-6 text-center bg-slate-50">
      <p className="text-slate-500 text-sm font-medium max-w-xs leading-relaxed">Die Menükarte konnte gerade nicht geladen werden. Bitte versuche es später erneut.</p>
    </div>
  );
  if (!data) return (
    <div className="min-h-[100dvh] flex items-center justify-center bg-slate-50">
      <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
    </div>
  );

  const { business, categories } = data;
  const logo = mediaUrl(business.logo_url);
  const initial = business.name?.[0] || "B";
  const showNav = categories.length >= 4;
  const feedbackAvailable = data.feedback_available === true;

  return (
    <BrandScope primary={business.primary_color} secondary={business.secondary_color} className="App">
      <div data-testid="menu-screen" className="min-h-[100dvh] bg-slate-50">
        <div className="w-full max-w-2xl mx-auto px-5 sm:px-8 pt-8 pb-10">
          {/* Compact branded header */}
          <header className="flex flex-col items-center text-center">
            <div className="h-16 w-16 rounded-2xl bg-white ring-1 ring-slate-200 shadow-sm flex items-center justify-center overflow-hidden">
              {logo
                ? <img src={logo} alt={business.name} className="h-full w-full object-contain" />
                : <span className="font-display font-extrabold text-2xl" style={{ color: "var(--brand-primary)" }}>{initial}</span>}
            </div>
            <h1 data-testid="menu-business-name" className="font-display font-extrabold text-2xl mt-3 tracking-tight text-slate-900 break-words">{business.name}</h1>
            <p className="text-xs font-bold uppercase tracking-[0.22em] mt-1" style={{ color: "var(--brand-primary)" }}>Menükarte</p>
          </header>

          {/* Category navigation (only when it adds value) */}
          {showNav && (
            <nav data-testid="menu-nav"
              className="mt-6 -mx-5 sm:mx-0 px-5 sm:px-0 flex gap-2 overflow-x-auto [&::-webkit-scrollbar]:hidden"
              style={{ scrollbarWidth: "none" }}>
              {categories.map((c) => (
                <button key={c.id} data-testid="menu-nav-item" onClick={() => scrollTo(c.id)}
                  className="shrink-0 whitespace-nowrap rounded-full px-4 py-2 text-sm font-bold text-slate-600 bg-white ring-1 ring-slate-200 press hover:ring-slate-300">
                  {c.name}
                </button>
              ))}
            </nav>
          )}

          {categories.length === 0 ? (
            <div data-testid="menu-empty-public" className="mt-20 text-center">
              <p className="text-slate-500 text-sm font-medium">Aktuell sind noch keine Einträge verfügbar.</p>
            </div>
          ) : (
            <div className="mt-8 space-y-9">
              {categories.map((c) => (
                <section key={c.id} id={secId(c.id)} data-testid="menu-section" className="scroll-mt-4">
                  <h2 data-testid="menu-section-heading"
                    className="font-display font-extrabold text-lg uppercase tracking-wide text-slate-900 pb-2 border-b-2"
                    style={{ borderColor: "var(--brand-primary)" }}>
                    {c.name}
                  </h2>
                  {c.note && (
                    <p data-testid="menu-section-note" className="text-[13px] text-slate-500 mt-2 break-words">{c.note}</p>
                  )}
                  <div className="mt-4 space-y-4">
                    {c.items.map((it) => <MenuItem key={it.id} it={it} />)}
                  </div>
                </section>
              ))}
            </div>
          )}

          {feedbackAvailable && (
            <div data-testid="menu-feedback-cta" className="mt-12 pt-8 border-t border-slate-200 flex flex-col items-center text-center">
              <h2 className="font-display font-extrabold text-lg text-slate-900">Wie war dein Besuch?</h2>
              <p className="text-sm text-slate-500 font-medium mt-1.5 leading-relaxed max-w-xs">
                Dein Feedback hilft uns, noch besser zu werden.
              </p>
              <Link data-testid="menu-feedback-link" to={`/feedback/${business.slug}`}
                className="mt-4 inline-flex items-center justify-center rounded-full px-6 py-2.5 text-sm font-bold text-white press"
                style={{ background: "var(--brand-primary)" }}>
                Feedback geben
              </Link>
            </div>
          )}

          <div className="mt-12 flex items-center justify-center gap-1.5 text-slate-300">
            <span className="font-semibold text-[11px]">Digitale Menükarte mit</span>
            <img src={bonsteloLogo} alt="Bonstelo" className="h-4 w-auto opacity-70" />
          </div>
        </div>
      </div>
    </BrandScope>
  );
};
